import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
} from '@mui/material';
import AgentColorPicker from 'components/@home/@settings/Tabs/Agents/AgentColorPicker';
import { colorFromString } from 'components/@home/@settings/Tabs/Agents/agentColorPalette';
import CloseIcon from 'mdi-react/CloseIcon';
import { bool, func, object } from 'prop-types';
import React, { useEffect, useState } from 'react';
import agentNameConflictMessage from 'utils/agentNameConflict';
import { I18n } from 'utils/i18n';

const NewAgentDialog = ({ open, onClose, onCreate, authCompany = null }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setColor('');
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  useEffect(() => {
    if (name.trim() && !color) {
      setColor(colorFromString(name));
    }
  }, [name, color]);

  const canSubmit = name.trim() && description.trim() && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim(),
        mode: 'agent',
        color: color || colorFromString(name),
        language: authCompany?.language || 'es',
      });
      onClose();
    } catch (err) {
      setError(agentNameConflictMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2, maxWidth: 440 } } }}
    >
      <IconButton
        onClick={onClose}
        aria-label={I18n.t('Common.close')}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'text.secondary',
        }}
      >
        <CloseIcon size={20} />
      </IconButton>
      <DialogContent sx={{ pt: 1 }}>
        <AgentColorPicker color={color} onChange={setColor} nameSeed={name} />
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            required
            label={I18n.t('AgenticChatFlow.AgentName')}
            placeholder={I18n.t('AgenticChatFlow.AgentNamePlaceholder')}
            value={name}
            onChange={e => {
              setName(e.target.value);
              if (error) setError('');
            }}
            margin="normal"
            size="small"
            error={Boolean(error)}
            helperText={error || undefined}
            slotProps={{ formHelperText: { 'data-testid': 'new-agent-error' } }}
          />
          <TextField
            fullWidth
            required
            label={I18n.t('AgenticChatFlow.Description')}
            placeholder={I18n.t('AgenticChatFlow.DescriptionPlaceholder')}
            value={description}
            onChange={e => setDescription(e.target.value)}
            margin="normal"
            size="small"
            multiline
            minRows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {I18n.t('Common.Cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {I18n.t('AgenticChatFlow.Next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewAgentDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  onCreate: func.isRequired,
  authCompany: object,
};

export default NewAgentDialog;
